import React, { useState } from 'react';
import { useLearningContext } from '@/contexts/LearningContext';
import { useFetchPendingTutorialsQuery, useFetchTutorialsByProductIdsQuery } from '@/api/tutorialsApi';
import VideoCard from '../VideoCard';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon, IdeaIcon } from '@hugeicons/core-free-icons';
import { Box, Button, ButtonGroup, CircularProgress, Divider, Drawer, IconButton, Paper, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import LearningEmptyState from './LeaningEmptyState';
import SuggestionDialog from './SuggestionDialog';
import SuggestionCard from './SuggesstionCard';
import { getInitials } from '@/utils/names';

export const SuggestButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: `${palette.primary.light}`,
        padding: 1.5,
        borderRadius: 3,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",

      }}>
      <Stack direction="row" alignItems="center" gap={2}>
        <HugeiconsIcon icon={IdeaIcon} color={palette.primary.main} />
        <Typography
          variant="h4"
          color="text.secondary"
        >
          ¿No encuentras lo que buscas?
        </Typography>
      </Stack>

      <Button
        variant='text'
        color='primary'
        onClick={onClick}
      >
        Sugerir un tema
      </Button>
    </Box>
  )
}

const LearningVideosDrawer: React.FC = () => {
  const context = useLearningContext();

  // Safe default values if used outside context (though it shouldn't be)
  const isDrawerOpen = context?.isDrawerOpen ?? false;
  const closeDrawer = context?.closeDrawer ?? (() => { });
  const activeProductIds = context?.activeProductIds ?? [];
  const [isSuggestionDialogOpen, setIsSuggestionDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const { data: tutorials, isFetching: isFetchingTutorials, error: tutorialsError } = useFetchTutorialsByProductIdsQuery(
    activeProductIds,
    { skip: !isDrawerOpen || activeTab !== 0 || activeProductIds.length === 0 } // Lazy fetch
  );

  const { data: suggestions, isFetching: isFetchingSuggestions, error: suggestionsError, } = useFetchPendingTutorialsQuery(undefined, {
    skip: !isDrawerOpen || activeTab !== 1,
  });


  const isFetching =
    activeTab === 0
      ? isFetchingTutorials
      : isFetchingSuggestions;

  const error =
    activeTab === 0
      ? tutorialsError
      : suggestionsError;


  const hasNoTutorials =
    activeProductIds.length === 0 ||
    (!isFetching && !error && tutorials?.length === 0);

  const hasNoSuggestions = !isFetching && !error && suggestions?.length === 0;

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  };

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={closeDrawer}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 500 }, p: 0 }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          padding: 2
        }}
      >
        <Typography variant="h2">Centro de Aprendizaje</Typography>
        <IconButton component={Paper} sx={{ backgroundColor: "#ffffff" }} elevation={2} onClick={closeDrawer} color="primary">
          <HugeiconsIcon icon={Cancel01Icon} />
        </IconButton>
      </Stack>

      <Divider />

      <ButtonGroup sx={{ padding: 2 }} >
        <Button fullWidth variant={activeTab === 0 ? 'contained' : 'outlined'} onClick={() => setActiveTab(0)}>Tutoriales</Button>
        <Button fullWidth variant={activeTab === 1 ? 'contained' : 'outlined'} onClick={() => setActiveTab(1)}>Sugerencias</Button>
      </ButtonGroup>
      <Divider />

      <Box sx={{ p: 2, height: 'calc(100vh - 70px)', overflowY: 'auto' }}>
        <Stack spacing={3} sx={{ height: '100%', mb: 2 }}>
          {activeTab === 0 ? (
            hasNoTutorials ? (
              <LearningEmptyState
                onSuggestTopic={() => setIsSuggestionDialogOpen(true)}
              />
            ) : (
              <Stack spacing={3}>
                <SuggestButton onClick={() => setIsSuggestionDialogOpen(true)} />
                {
                  tutorials?.map((tutorial) => (
                    <VideoCard tutorial={tutorial} />
                  ))}
              </ Stack>
            )
          ) : (
            hasNoSuggestions ? (
              <>
                <LearningEmptyState
                  onSuggestTopic={() => setIsSuggestionDialogOpen(true)}
                />
              </>
            ) : (
              <>
                <SuggestButton onClick={() => setIsSuggestionDialogOpen(true)} />
                {

                  suggestions?.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      initials={suggestion.email ? getInitials(suggestion.email) : "N/A"}
                    />

                  ))
                }
              </>
            )
          )}
        </Stack>
      </Box>
      <SuggestionDialog
        open={isSuggestionDialogOpen}
        onClose={() => setIsSuggestionDialogOpen(false)}
      />
    </Drawer >

  );
};

export default LearningVideosDrawer;
